import { useEffect, useRef, useState, type WheelEvent, type MouseEvent } from 'react';
import {
  ZoomIn,
  ZoomOut,
  RotateCw,
  RotateCcw,
  RefreshCw,
  SlidersHorizontal,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { Checkbox } from '@/components/ui/checkbox';
import { cn } from '@/lib/utils';

interface ImageViewerProps {
  src: string;
  alt?: string;
  className?: string;
}

export function ImageViewer({ src, alt, className }: ImageViewerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  // fitScale = el scale que hace que la imagen llene el contenedor preservando
  // aspect ratio (object-contain behavior). Es el zoom inicial y el target de
  // reset(). Null hasta que img + container midan, ahí seteamos.
  const [fitScale, setFitScale] = useState<number | null>(null);
  const [scale, setScale] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [tx, setTx] = useState(0);
  const [ty, setTy] = useState(0);
  const [brightness, setBrightness] = useState(100);
  const [contrast, setContrast] = useState(100);
  const [saturate, setSaturate] = useState(100);
  const [invert, setInvert] = useState(false);
  const [dragging, setDragging] = useState(false);
  // Filtros (brillo/contraste/etc) colapsados por default — ocupan mucho
  // alto y la mayoría de las facturas se leen bien sin tocarlos. El
  // contador los abre solo si la imagen está oscura/borrosa.
  const [showAdjust, setShowAdjust] = useState(false);
  const dragStart = useRef({ x: 0, y: 0, tx: 0, ty: 0 });

  /** Calcula scale que llena el contenedor preservando aspect ratio.
   *  Considera la rotación: cuando la imagen está rotada 90°/270° los ejes
   *  se invierten para el cálculo de fit. */
  function computeFitScale(rotationDeg = rotation): number {
    const img = imgRef.current;
    const container = containerRef.current;
    if (!img || !container) return 1;
    const naturalW = img.naturalWidth;
    const naturalH = img.naturalHeight;
    if (!naturalW || !naturalH) return 1;
    const containerW = container.clientWidth;
    const containerH = container.clientHeight;
    if (!containerW || !containerH) return 1;
    // Cuando rotación es 90° o 270°, los ejes naturales se intercambian
    // para el cálculo del bounding box visible
    const rotated = rotationDeg === 90 || rotationDeg === 270;
    const effW = rotated ? naturalH : naturalW;
    const effH = rotated ? naturalW : naturalH;
    return Math.min(containerW / effW, containerH / effH);
  }

  function applyFit() {
    const next = computeFitScale();
    setFitScale(next);
    setScale(next);
    setTx(0);
    setTy(0);
  }

  function onImageLoad() {
    applyFit();
  }

  // Recalcular fit cuando el contenedor cambia de tamaño (sidebar colapsa,
  // ventana resize, etc). NO toca el scale actual del usuario — solo
  // actualiza el fitScale para que el próximo reset funcione bien.
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const observer = new ResizeObserver(() => {
      const newFit = computeFitScale();
      setFitScale(newFit);
    });
    observer.observe(container);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Recalcular fit cuando rota — los ejes cambian
  useEffect(() => {
    const newFit = computeFitScale(rotation);
    setFitScale(newFit);
    setScale(newFit);
    setTx(0);
    setTy(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rotation]);

  function reset() {
    // Reset vuelve al fit, NO al scale=1 (que daría tamaño natural del archivo
    // y dejaría espacio negro alrededor en imágenes chicas).
    const next = computeFitScale();
    setFitScale(next);
    setScale(next);
    setRotation(0);
    setTx(0);
    setTy(0);
    setBrightness(100);
    setContrast(100);
    setSaturate(100);
    setInvert(false);
  }

  function zoomIn() {
    setScale((s) => Math.min(8, s * 1.25));
  }
  function zoomOut() {
    const minScale = (fitScale ?? 1) * 0.5; // permitir zoom out hasta 50% del fit
    setScale((s) => Math.max(minScale, s / 1.25));
  }
  function rotateRight() {
    setRotation((r) => (r + 90) % 360);
  }
  function rotateLeft() {
    setRotation((r) => (r - 90 + 360) % 360);
  }

  function onWheel(e: WheelEvent<HTMLDivElement>) {
    e.preventDefault();
    const delta = e.deltaY < 0 ? 1.1 : 0.9;
    const minScale = (fitScale ?? 1) * 0.5;
    setScale((s) => Math.min(8, Math.max(minScale, s * delta)));
  }

  function onMouseDown(e: MouseEvent<HTMLDivElement>) {
    setDragging(true);
    dragStart.current = { x: e.clientX, y: e.clientY, tx, ty };
  }
  function onMouseMove(e: MouseEvent<HTMLDivElement>) {
    if (!dragging) return;
    setTx(dragStart.current.tx + (e.clientX - dragStart.current.x));
    setTy(dragStart.current.ty + (e.clientY - dragStart.current.y));
  }
  function onMouseUp() {
    setDragging(false);
  }

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.target as HTMLElement)?.tagName?.match(/INPUT|TEXTAREA|SELECT/)) return;
      if (e.key === '0') reset();
      if (e.key === '+' || e.key === '=') zoomIn();
      if (e.key === '-' || e.key === '_') zoomOut();
      if (e.key === 'r') rotateRight();
      if (e.key === 'R' || e.key === 'l') rotateLeft();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const filter = `brightness(${brightness}%) contrast(${contrast}%) saturate(${saturate}%) ${
    invert ? 'invert(1)' : ''
  }`;

  return (
    <div className={cn('flex flex-col h-full', className)}>
      <div className="flex flex-wrap items-center gap-2 border-b border-border bg-muted px-3 py-2">
        <Button size="icon" variant="outline" onClick={zoomOut} title="Zoom out (-)">
          <ZoomOut />
        </Button>
        <span className="text-xs font-mono text-muted-foreground min-w-[3.5rem] text-center">
          {fitScale ? Math.round((scale / fitScale) * 100) : Math.round(scale * 100)}%
        </span>
        <Button size="icon" variant="outline" onClick={zoomIn} title="Zoom in (+)">
          <ZoomIn />
        </Button>
        <div className="w-px h-6 bg-line mx-1" />
        <Button size="icon" variant="outline" onClick={rotateLeft} title="Rotar izquierda (L)">
          <RotateCcw />
        </Button>
        <Button size="icon" variant="outline" onClick={rotateRight} title="Rotar derecha (R)">
          <RotateCw />
        </Button>
        <div className="w-px h-6 bg-line mx-1" />
        <Button size="sm" variant="ghost" onClick={reset} title="Reset (0)">
          <RefreshCw />
          Reset
        </Button>
        <div className="w-px h-6 bg-line mx-1" />
        {/* Toggle de ajustes — colapsado por default para dar más espacio
            a la imagen. */}
        <Button
          size="sm"
          variant={showAdjust ? 'secondary' : 'ghost'}
          onClick={() => setShowAdjust((s) => !s)}
          title="Ajustes de imagen (brillo, contraste, etc.)"
        >
          <SlidersHorizontal />
          Ajustes
        </Button>
      </div>

      {showAdjust && (
        <div className="grid grid-cols-3 gap-3 border-b border-border bg-muted px-3 py-2 text-xs">
          <SliderField label="Brillo" value={brightness} onChange={setBrightness} min={50} max={200} />
          <SliderField label="Contraste" value={contrast} onChange={setContrast} min={50} max={200} />
          <SliderField label="Saturación" value={saturate} onChange={setSaturate} min={0} max={200} />
          <label className="flex items-center gap-2 text-xs col-span-3 cursor-pointer">
            <Checkbox
              checked={invert}
              onCheckedChange={(v) => setInvert(v === true)}
              id="invert-colors"
            />
            Invertir colores (útil para facturas con fondo oscuro)
          </label>
        </div>
      )}

      <div
        ref={containerRef}
        className="relative flex-1 overflow-hidden bg-[#1a1320] select-none"
        onWheel={onWheel}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
        onMouseLeave={onMouseUp}
        style={{ cursor: dragging ? 'grabbing' : 'grab' }}
      >
        <img
          ref={imgRef}
          src={src}
          alt={alt}
          draggable={false}
          onLoad={onImageLoad}
          className="absolute top-1/2 left-1/2 max-w-none origin-center pointer-events-none"
          style={{
            transform: `translate(-50%, -50%) translate(${tx}px, ${ty}px) rotate(${rotation}deg) scale(${scale})`,
            filter,
            transition: dragging ? 'none' : 'transform 50ms ease-out, filter 100ms linear',
          }}
        />
      </div>

    </div>
  );
}

function SliderField({
  label,
  value,
  onChange,
  min,
  max,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  min: number;
  max: number;
}) {
  return (
    <div>
      <div className="flex justify-between mb-1">
        <span className="text-muted-foreground">{label}</span>
        <span className="font-mono text-foreground">{value}%</span>
      </div>
      <Slider
        min={min}
        max={max}
        step={1}
        value={[value]}
        onValueChange={(v) => onChange(v[0] ?? min)}
      />
    </div>
  );
}
